import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import * as db from '@/lib/data-service';
import { QUIZ_QUESTIONS, getQuizType } from '@/lib/constants';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronLeft, Send, Calendar, Flag, ThumbsUp, MoreVertical, LogOut, Sparkles, Clock, Heart } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { toast } from 'sonner';

const INTENT_OPTIONS = [
  { value: 'friendship', label: 'Friendship', sublabel: 'Looking to connect and build something genuine', icon: '👋' },
  { value: 'open', label: 'Open to both', sublabel: "Not sure yet — happy to see where this goes", icon: '✨' },
  { value: 'relationship', label: 'Relationship', sublabel: 'Looking for something serious and long-term', icon: '❤️' },
];

function getIntentReveal(a, b) {
  const pair = [a, b].sort().join('+');
  switch (pair) {
    case 'relationship+relationship': return { icon: '❤️', heading: "You're both looking for the same thing.", subtext: "You both chose Relationship. A great place to start." };
    case 'friendship+friendship': return { icon: '🤝', heading: "You're here to connect.", subtext: "You both chose Friendship. Genuine connections start here." };
    case 'open+open': return { icon: '✨', heading: "You're both open to wherever this goes.", subtext: "No pressure — just see what unfolds." };
    case 'friendship+relationship': return { icon: '💬', heading: "You're looking for different things.", subtext: "That's okay — connections can surprise you. Keep talking." };
    case 'open+relationship': return { icon: '✨', heading: "One of you is open to either.", subtext: "Talk and find out what feels right." };
    case 'friendship+open': return { icon: '👋', heading: "One of you is open to either.", subtext: "Friendship is a great foundation — see where it leads." };
    default: return { icon: '✨', heading: "You've both shared what you're hoping for.", subtext: "Keep chatting and see where it goes." };
  }
}

export default function Chat() {
  const { matchId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [match, setMatch] = useState(null);
  const [otherProfile, setOtherProfile] = useState(null);
  const [otherPhotos, setOtherPhotos] = useState([]);
  const [showDateModal, setShowDateModal] = useState(false);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [showReportMenu, setShowReportMenu] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);
  const [promptContext, setPromptContext] = useState(null);
  const [showConversationLimitModal, setShowConversationLimitModal] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [exitLoading, setExitLoading] = useState(false);
  const [myIntent, setMyIntent] = useState(null);
  const [theirIntent, setTheirIntent] = useState(null);
  const [intentRevealed, setIntentRevealed] = useState(false);
  const [showIntentSelect, setShowIntentSelect] = useState(false);
  const [selectedIntentOption, setSelectedIntentOption] = useState(null);

  // Quiz state
  const [quizSession, setQuizSession] = useState(null);
  const [quizState, setQuizState] = useState('none');
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
  const [myAnswers, setMyAnswers] = useState({});
  const [quizResults, setQuizResults] = useState(null);
  const [quizTimer, setQuizTimer] = useState(7);
  const [quizType, setQuizType] = useState(null);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [selectedAnswerIdx, setSelectedAnswerIdx] = useState(null);

  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const intentPollRef = useRef(null);
  const quizPollRef = useRef(null);
  const timerRef = useRef(null);

  const REPORT_REASONS = ['Not Jain', 'Fake profile', 'Harassment', 'Inappropriate content'];

  const startIntentPoll = (mid, uid, myCurrentIntent) => {
    if (intentPollRef.current) clearInterval(intentPollRef.current);
    intentPollRef.current = setInterval(async () => {
      const fresh = await db.getMatchIntents(mid);
      const theirRow = fresh.find(r => r.user_id !== uid);
      if (theirRow) {
        setTheirIntent(theirRow.intent);
        setIntentRevealed(true);
        clearInterval(intentPollRef.current);
        intentPollRef.current = null;
        if (myCurrentIntent) {
          const resolvedQT = getQuizType(myCurrentIntent, theirRow.intent);
          if (resolvedQT) {
            setQuizType(resolvedQT);
            setQuizQuestions(QUIZ_QUESTIONS[resolvedQT] || []);
            const session = await db.getQuizSession(mid);
            if (!session) {
              setQuizState('nudge');
            } else {
              setQuizSession(session);
              setQuizState(
                session.status === 'completed' ? 'completed' :
                session.initiated_by === uid ? 'waiting_other' : 'nudge'
              );
            }
          }
        }
      }
    }, 10000);
  };

  useEffect(() => {
    let cancelled = false;

    async function initChat() {
      await db.syncFromSupabase(user.id);
      const m = db.getMatch(matchId);
      if (!m) { navigate('/messages'); return; }
      if (cancelled) return;

      setMatch(m);
      const otherId = m.user_1 === user.id ? m.user_2 : m.user_1;
      setOtherProfile(db.getProfile(otherId));
      setOtherPhotos(db.getPhotos(otherId));

      const activeConvoCount = db.countActiveConversations(user.id);
      if (activeConvoCount >= 2) {
        const matchAgeMs = Date.now() - new Date(m.created_at).getTime();
        if (matchAgeMs < 60000) setShowConversationLimitModal(true);
      }

      const intents = await db.getMatchIntents(matchId);
      const myRow = intents.find(r => r.user_id === user.id);
      const theirRow = intents.find(r => r.user_id !== user.id);

      if (myRow) setMyIntent(myRow.intent);
      if (theirRow) setTheirIntent(theirRow.intent);

      if (myRow && theirRow) {
        setIntentRevealed(true);
      } else if (myRow && !theirRow) {
        startIntentPoll(matchId, user.id, myRow.intent);
      } else if (!myRow) {
        setShowIntentSelect(true);
      }

      // Quiz initialization
      const resolvedQuizType = myRow && theirRow ? getQuizType(myRow.intent, theirRow.intent) : null;
      setQuizType(resolvedQuizType);
      const resolvedQuizQuestions = resolvedQuizType ? (QUIZ_QUESTIONS[resolvedQuizType] || []) : [];
      setQuizQuestions(resolvedQuizQuestions);

      if (resolvedQuizType) {
        const session = await db.getQuizSession(matchId);
        if (cancelled) return;

        if (session) {
          setQuizSession(session);
          if (session.status === 'completed') {
            const responses = await db.getQuizResponses(session.id);
            if (!cancelled) {
              const pct = db.calculateCompatibilityPercent(responses, user.id, session.initiated_by === user.id
                ? responses.find(r => r.user_id !== user.id)?.user_id
                : session.initiated_by, resolvedQuizQuestions.length);
              setQuizResults({ percent: pct, quizType: resolvedQuizType });
              setQuizState('completed');
            }
          } else if (session.status === 'active') {
            const responses = await db.getQuizResponses(session.id);
            if (!cancelled) {
              const myResps = responses.filter(r => r.user_id === user.id);
              const answeredKeys = new Set(myResps.map(r => r.question_key));
              const myAnswersMap = {};
              myResps.forEach(r => { myAnswersMap[r.question_key] = r.answer_index; });
              setMyAnswers(myAnswersMap);

              if (myResps.length >= resolvedQuizQuestions.length) {
                setQuizState('waiting_other');
              } else {
                setQuizState('active');
                const nextIdx = resolvedQuizQuestions.findIndex(q => !answeredKeys.has(q.key));
                setCurrentQuestionIdx(nextIdx >= 0 ? nextIdx : 0);
                setQuizTimer(7);
              }
            }
          } else if (session.status === 'waiting') {
            if (session.initiated_by === user.id) {
              setQuizState('waiting_other');
            } else {
              setQuizState('nudge');
            }
          }
        } else {
          setQuizState('nudge');
        }
      }

      const allMatches = db.getMatches(user.id);
      const fullMatch = allMatches.find(mm => mm.id === matchId);
      if (fullMatch?.promptInteraction?.prompt_index) {
        const p = db.getProfile(otherId);
        const idx = fullMatch.promptInteraction.prompt_index;
        const q = p?.[`prompt_${idx}_q`];
        const a = p?.[`prompt_${idx}_a`];
        if (q && a) setPromptContext({ question: q, answer: a });
      }

      setMessages(db.getMessages(matchId));

      pollRef.current = setInterval(async () => {
        await db.syncMessagesForUser(user.id);
        setMessages(db.getMessages(matchId));
      }, 2000);
    }

    initChat();

    return () => {
      cancelled = true;
      clearInterval(pollRef.current);
      clearInterval(intentPollRef.current);
      clearInterval(quizPollRef.current);
      clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, user, navigate]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // Quiz timer
  useEffect(() => {
    if (quizState !== 'active') return;
    setQuizTimer(7);
    setSelectedAnswerIdx(null);
    clearInterval(timerRef.current);
    let count = 7;
    timerRef.current = setInterval(() => {
      count--;
      setQuizTimer(count);
      if (count <= 0) {
        clearInterval(timerRef.current);
        handleAnswer(-1);
      }
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizState, currentQuestionIdx]);

  // Quiz waiting_other poll
  useEffect(() => {
    if (quizState !== 'waiting_other' || !quizSession) return;
    clearInterval(quizPollRef.current);
    quizPollRef.current = setInterval(async () => {
      const fresh = await db.getQuizSession(matchId);
      if (fresh?.status === 'completed') {
        clearInterval(quizPollRef.current);
        const responses = await db.getQuizResponses(fresh.id);
        const theirUserId = fresh.initiated_by === user.id
          ? responses.find(r => r.user_id !== user.id)?.user_id
          : fresh.initiated_by;
        const pct = db.calculateCompatibilityPercent(responses, user.id, theirUserId, quizQuestions.length);
        setQuizResults({ percent: pct, quizType });
        setQuizState('completed');
        setQuizSession(fresh);
      }
    }, 15000);
    return () => clearInterval(quizPollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizState, quizSession]);

  const handleAnswer = async (answerIndex) => {
    clearInterval(timerRef.current);
    const question = quizQuestions[currentQuestionIdx];
    if (!question || !quizSession) return;

    let updatedAnswers = { ...myAnswers };
    if (answerIndex >= 0) {
      await db.saveQuizResponse(quizSession.id, user.id, question.key, answerIndex);
      updatedAnswers[question.key] = answerIndex;
      setMyAnswers(updatedAnswers);
    }

    if (currentQuestionIdx < quizQuestions.length - 1) {
      setCurrentQuestionIdx(idx => idx + 1);
      setQuizTimer(7);
      setSelectedAnswerIdx(null);
    } else {
      const responses = await db.getQuizResponses(quizSession.id);
      const otherResps = responses.filter(r => r.user_id !== user.id);
      if (otherResps.length >= quizQuestions.length) {
        const theirUserId = otherResps[0].user_id;
        await db.completeQuizSession(quizSession.id);
        const pct = db.calculateCompatibilityPercent(responses, user.id, theirUserId, quizQuestions.length);
        setQuizResults({ percent: pct, quizType });
        setQuizState('completed');
      } else {
        setQuizState('waiting_other');
      }
    }
  };

  const handleSelectAnswer = (idx) => {
    clearInterval(timerRef.current);
    setSelectedAnswerIdx(idx);
    setTimeout(() => handleAnswer(idx), 500);
  };

  const send = async () => {
    if (!input.trim()) return;
    db.sendMessage(matchId, user.id, input.trim());
    setInput('');
    await db.syncMessagesForUser(user.id);
    setMessages(db.getMessages(matchId));
  };

  const handleLogDate = () => {
    db.updateMatch(matchId, { date_logged: true });
    setMatch(prev => ({ ...prev, date_logged: true }));
    setShowDateModal(false);
    toast('Date logged! We\'ll check in afterwards.');
  };

  const handleFlag = () => {
    if (!otherProfile) return;
    db.createReport(user.id, otherProfile.id, matchId, 'post_date_flag');
    db.updateMatch(matchId, { date_outcome: 'flagged' });
    setShowFlagModal(false);
    toast('Report submitted. Thank you for helping keep Jiv safe.');
  };

  const handlePositive = () => {
    db.updateMatch(matchId, { date_outcome: 'positive' });
    setMatch(prev => ({ ...prev, date_outcome: 'positive' }));
    toast('Great to hear! Happy for you both.');
  };

  const handleReport = (reason) => {
    if (!otherProfile) return;
    db.createReport(user.id, otherProfile.id, matchId, reason);
    setShowReportDialog(false);
    setShowReportMenu(false);
    setSelectedReason(null);
    toast('Report submitted. Thank you for helping keep Jiv safe.');
  };

  const handleExitChat = async () => {
    if (!match) return;
    setExitLoading(true);
    const otherUserId = match.user_1 === user.id ? match.user_2 : match.user_1;
    const result = await db.exitChat(matchId, user.id, otherUserId);
    setExitLoading(false);
    if (result.success) {
      toast('Chat ended.');
      navigate('/matches');
    } else {
      setShowExitDialog(false);
      toast('Something went wrong. Try again.');
    }
  };

  const handleSubmitIntent = async () => {
    if (!selectedIntentOption) return;
    const ok = await db.createMatchIntent(matchId, user.id, selectedIntentOption);
    if (!ok) { toast('Could not save — please try again.'); return; }
    setMyIntent(selectedIntentOption);
    setShowIntentSelect(false);
    setSelectedIntentOption(null);
    const intents = await db.getMatchIntents(matchId);
    const theirRow = intents.find(r => r.user_id !== user.id);
    if (theirRow) {
      setTheirIntent(theirRow.intent);
      setIntentRevealed(true);
      const resolvedQT = getQuizType(selectedIntentOption, theirRow.intent);
      if (resolvedQT) {
        setQuizType(resolvedQT);
        setQuizQuestions(QUIZ_QUESTIONS[resolvedQT] || []);
        const session = await db.getQuizSession(matchId);
        if (!session) {
          setQuizState('nudge');
        } else {
          setQuizSession(session);
          if (session.status === 'completed') {
            const responses = await db.getQuizResponses(session.id);
            const theirId = session.initiated_by === user.id
              ? responses.find(r => r.user_id !== user.id)?.user_id
              : session.initiated_by;
            const pct = db.calculateCompatibilityPercent(responses, user.id, theirId, (QUIZ_QUESTIONS[resolvedQT] || []).length);
            setQuizResults({ percent: pct, quizType: resolvedQT });
            setQuizState('completed');
          } else if (session.status === 'waiting') {
            setQuizState(session.initiated_by === user.id ? 'waiting_other' : 'nudge');
          } else {
            setQuizState('nudge');
          }
        }
      }
    } else {
      startIntentPoll(matchId, user.id, selectedIntentOption);
    }
  };

  const canLogDate = messages.length >= 5 && match && !match.date_logged;
  const intentReveal = intentRevealed && myIntent && theirIntent ? getIntentReveal(myIntent, theirIntent) : null;

  const getCompatibilityMessage = (pct) => {
    if (pct >= 60) return "You're on the same page on a lot of things. That's a good place to start.";
    if (pct >= 40) return "You see some things differently — that's a conversation worth having.";
    return "Differences are interesting. The best conversations start where two worlds meet.";
  };

  return (
    <div className="h-[100dvh] flex flex-col bg-background max-w-md mx-auto relative" data-testid="chat-screen">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-background shrink-0">
        <button onClick={() => navigate('/messages')} data-testid="chat-back"><ChevronLeft className="h-5 w-5 text-foreground" /></button>
        <div className="w-9 h-9 rounded-full overflow-hidden bg-muted shrink-0">
          {otherPhotos[0]
            ? <img src={otherPhotos[0].url} className="w-full h-full object-cover no-screenshot" alt="" onContextMenu={(e) => e.preventDefault()} draggable={false} />
            : <div className="w-full h-full flex items-center justify-center text-xs font-bold text-muted-foreground">{otherProfile?.display_name?.[0]}</div>
          }
        </div>
        <span className="font-semibold text-foreground text-sm flex-1">{otherProfile?.display_name}</span>
        <div className="flex items-center gap-2">
          {canLogDate && (
            <button onClick={() => setShowDateModal(true)} className="text-muted-foreground hover:text-foreground" data-testid="log-date-btn">
              <Calendar className="h-5 w-5" />
            </button>
          )}
          {match?.date_logged && !match?.date_outcome && (
            <>
              <button onClick={handlePositive} className="text-green-600 hover:text-green-700" data-testid="date-positive-btn" title="Date went well">
                <ThumbsUp className="h-5 w-5" />
              </button>
              <button onClick={() => setShowFlagModal(true)} className="text-muted-foreground hover:text-destructive" data-testid="date-flag-btn" title="Something felt off">
                <Flag className="h-5 w-5" />
              </button>
            </>
          )}
          <button onClick={() => setShowExitDialog(true)} className="text-muted-foreground hover:text-foreground" data-testid="exit-chat-btn">
            <LogOut className="h-5 w-5" />
          </button>
          <Sheet open={showReportMenu} onOpenChange={setShowReportMenu}>
            <SheetTrigger asChild>
              <button className="text-muted-foreground hover:text-foreground" data-testid="report-menu-btn">
                <MoreVertical className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="rounded-t-[20px]">
              <SheetHeader><SheetTitle>Report this person</SheetTitle></SheetHeader>
              <div className="py-4 space-y-2">
                {REPORT_REASONS.map(reason => (
                  <button
                    key={reason}
                    onClick={() => { setSelectedReason(reason); setShowReportDialog(true); }}
                    className="w-full text-left px-4 py-3 rounded-xl border border-border hover:bg-muted transition-colors text-sm"
                    data-testid={`report-reason-${reason.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {reason}
                  </button>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Waiting banner */}
      {myIntent && !intentRevealed && (
        <div className="px-4 py-2.5 bg-[#FBE7F3] shrink-0" data-testid="intent-waiting-banner">
          <p className="text-[13px] text-[#C857A8] text-center">Waiting for them to share what they're hoping for...</p>
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-auto p-4 space-y-2" data-testid="chat-messages">
        {/* Prompt context card */}
        {promptContext && (
          <div className="p-3 rounded-xl border border-border bg-card mb-4" data-testid="prompt-context">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{promptContext.question}</p>
            <p className="text-sm text-foreground mt-1">{promptContext.answer}</p>
          </div>
        )}

        {/* Intent reveal card */}
        {intentReveal && (
          <div className="p-4 rounded-xl border text-center mb-4" style={{ borderColor: 'rgba(200,87,168,0.25)', background: '#FBE7F3' }} data-testid="intent-reveal">
            <p className="text-2xl mb-2">{intentReveal.icon}</p>
            <p className="text-sm font-semibold text-[#1A1814] leading-snug">{intentReveal.heading}</p>
            <p className="text-[13px] text-[#7A7672] mt-1">{intentReveal.subtext}</p>
            <p className="text-[11px] text-muted-foreground mt-3">This is only visible to you two.</p>
          </div>
        )}

        {/* Quiz nudge — inline in messages area */}
        {quizState === 'nudge' && quizType && (
          <div
            style={{ border: '0.5px solid rgba(200,87,168,0.25)', borderRadius: 16, padding: 16, background: '#FBE7F3', marginBottom: 8 }}
            data-testid="quiz-nudge"
          >
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 shrink-0 mt-0.5" style={{ color: '#C857A8' }} />
              <div className="flex-1">
                <p className="text-[13px] font-semibold text-[#1A1814]">Find out what you have in common</p>
                <p className="text-[11px] text-[#7A7672] mt-0.5">Answer 7 questions together</p>
                <Button
                  size="sm"
                  className="mt-3 rounded-full text-xs"
                  style={{ background: '#C857A8', color: 'white' }}
                  onClick={async () => {
                    if (!quizSession) {
                      const session = await db.createQuizSession(matchId, user.id, quizType);
                      if (session) {
                        setQuizSession(session);
                        setCurrentQuestionIdx(0);
                        setQuizTimer(7);
                        setSelectedAnswerIdx(null);
                        setQuizState('active');
                      }
                    } else {
                      await db.acceptQuizSession(quizSession.id);
                      setCurrentQuestionIdx(0);
                      setQuizTimer(7);
                      setSelectedAnswerIdx(null);
                      setQuizState('active');
                    }
                  }}
                  data-testid="quiz-start-btn"
                >
                  {quizSession ? 'Take the quiz' : 'Start the quiz'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Quiz waiting — inline in messages area */}
        {quizState === 'waiting_other' && (
          <div
            style={{ border: '0.5px solid rgba(200,87,168,0.25)', borderRadius: 16, padding: '12px 16px', background: '#FBE7F3', marginBottom: 8 }}
            className="flex items-center gap-3"
            data-testid="quiz-waiting"
          >
            <Clock className="h-5 w-5 shrink-0" style={{ color: '#C857A8' }} />
            <p className="text-[13px] text-[#7A7672]">
              {quizSession?.status === 'waiting'
                ? `Waiting for ${otherProfile?.display_name} to take the quiz`
                : `Waiting for ${otherProfile?.display_name} to finish the quiz`}
            </p>
          </div>
        )}

        {/* Quiz results card */}
        {quizState === 'completed' && quizResults && (
          <div
            style={{ border: '0.5px solid rgba(200,87,168,0.25)', borderRadius: 16, padding: 16, background: '#FBE7F3', margin: '0 0 8px' }}
            data-testid="quiz-results-card"
          >
            {quizResults.quizType === 'bridge' ? (
              <div className="text-center">
                <Heart className="h-6 w-6 mx-auto mb-2" style={{ color: '#C857A8' }} />
                <p className="text-sm font-semibold text-[#1A1814]">Connection potential</p>
                <p className="text-[13px] text-[#7A7672] mt-1">You share something meaningful — keep the conversation going.</p>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-[10px] uppercase tracking-wider text-[#A8A5A0] mb-2">Compatibility</p>
                <p style={{ fontSize: 36, fontWeight: 500, color: '#C857A8', fontFamily: 'var(--font-serif, serif)', lineHeight: 1.1 }}>
                  {quizResults.percent}%
                </p>
                <p className="text-[13px] text-[#7A7672] mt-2 leading-relaxed">{getCompatibilityMessage(quizResults.percent)}</p>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground text-center mt-3">Only visible to you two</p>
          </div>
        )}

        {messages.length === 0 && !intentReveal && (
          <p className="text-center text-muted-foreground text-sm py-8">No messages yet. Say hello!</p>
        )}

        {messages.map(msg => {
          const mine = msg.sender_id === user.id;
          return (
            <div key={msg.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`message-${msg.id}`}>
              <div className={`max-w-[75%] px-3.5 py-2.5 text-sm leading-relaxed ${
                mine
                  ? 'bg-primary text-primary-foreground rounded-2xl rounded-tr-sm'
                  : 'bg-card border border-border text-foreground rounded-2xl rounded-tl-sm'
              }`}>
                {msg.content}
                <p className={`text-[10px] mt-1 ${mine ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
                  {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Input */}
      <div className="flex items-center gap-2 px-4 py-3 border-t border-border bg-background shrink-0">
        <Input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && send()}
          placeholder={messages.length === 0 ? 'Reply to their prompt, or say hello...' : 'Type a message...'}
          className="h-10 rounded-full text-sm"
          data-testid="chat-input"
        />
        <Button onClick={send} size="icon" className="h-10 w-10 rounded-full shrink-0" disabled={!input.trim()} data-testid="chat-send">
          <Send className="h-4 w-4" />
        </Button>
      </div>

      {/* Active quiz overlay */}
      {quizState === 'active' && quizQuestions.length > 0 && (
        <div
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, minHeight: '100%', background: 'var(--background)', zIndex: 50, overflowY: 'auto' }}
          data-testid="quiz-active-overlay"
        >
          <div className="flex flex-col h-full px-5 pt-6 pb-8" style={{ minHeight: '100dvh' }}>
            {/* Progress bar */}
            <div className="mb-4">
              <p className="text-[11px] text-muted-foreground text-center mb-2">
                Question {currentQuestionIdx + 1} of {quizQuestions.length}
              </p>
              <div style={{ height: 4, borderRadius: 2, background: '#F5D5EC' }}>
                <div
                  style={{
                    height: '100%',
                    borderRadius: 2,
                    background: '#C857A8',
                    width: `${((currentQuestionIdx + 1) / quizQuestions.length) * 100}%`,
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
            </div>

            {/* Timer */}
            <div className="flex justify-center mb-6">
              <div style={{ position: 'relative', width: 56, height: 56 }}>
                <svg width={56} height={56} style={{ position: 'absolute', top: 0, left: 0, transform: 'rotate(-90deg)' }}>
                  <circle cx={28} cy={28} r={24} fill="none" stroke="#F5D5EC" strokeWidth={3} />
                  <circle
                    cx={28} cy={28} r={24} fill="none"
                    stroke="#C857A8" strokeWidth={3}
                    strokeDasharray={`${2 * Math.PI * 24}`}
                    strokeDashoffset={`${2 * Math.PI * 24 * (1 - quizTimer / 7)}`}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dashoffset 0.9s linear' }}
                  />
                </svg>
                <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 600, color: '#C857A8' }}>
                  {quizTimer}
                </span>
              </div>
            </div>

            {/* Question */}
            <p style={{ fontSize: 18, fontWeight: 500, textAlign: 'center', maxWidth: 320, margin: '0 auto 24px', lineHeight: 1.4, color: '#1A1814' }}>
              {quizQuestions[currentQuestionIdx]?.text}
            </p>

            {/* Options */}
            <div className="flex flex-col gap-3" style={{ maxWidth: 360, margin: '0 auto', width: '100%' }}>
              {quizQuestions[currentQuestionIdx]?.options.map((opt, idx) => (
                <button
                  key={idx}
                  onClick={() => selectedAnswerIdx === null && handleSelectAnswer(idx)}
                  style={{
                    padding: '12px 16px',
                    borderRadius: 12,
                    fontSize: 14,
                    textAlign: 'left',
                    border: selectedAnswerIdx === idx ? '1.5px solid #C857A8' : '0.5px solid var(--border)',
                    background: selectedAnswerIdx === idx ? '#FBE7F3' : 'var(--background)',
                    color: '#1A1814',
                    cursor: selectedAnswerIdx !== null ? 'default' : 'pointer',
                    transition: 'border-color 0.15s, background 0.15s',
                  }}
                  data-testid={`quiz-option-${idx}`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Intent selection overlay */}
      {showIntentSelect && (
        <div className="absolute inset-0 z-40 bg-black/60 flex items-center justify-center px-6" data-testid="intent-select-overlay">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="w-14 h-14 rounded-full overflow-hidden bg-muted mx-auto mb-4 flex items-center justify-center">
              {otherPhotos[0]
                ? <img src={otherPhotos[0].url} className="w-full h-full object-cover" alt="" onContextMenu={(e) => e.preventDefault()} draggable={false} />
                : <span className="text-lg font-bold text-muted-foreground">{otherProfile?.display_name?.[0]}</span>
              }
            </div>
            <h2 className="text-base font-semibold text-[#1A1814] text-center mb-1">Before you start chatting...</h2>
            <p className="text-[13px] text-[#7A7672] text-center mb-5">
              What are you hoping this becomes?<br />Only revealed when you both answer.
            </p>
            <div className="space-y-3">
              {INTENT_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setSelectedIntentOption(opt.value)}
                  className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-colors ${
                    selectedIntentOption === opt.value
                      ? 'border-[#C857A8] bg-[#FBE7F3]'
                      : 'border-border hover:bg-[#FBE7F3]'
                  }`}
                  data-testid={`intent-option-${opt.value}`}
                >
                  <span className="text-base mr-2">{opt.icon}</span>
                  <span className="text-sm font-semibold text-[#1A1814]">{opt.label}</span>
                  <p className="text-[12px] text-[#7A7672] mt-0.5 pl-7">{opt.sublabel}</p>
                </button>
              ))}
            </div>
            {selectedIntentOption && (
              <Button
                onClick={handleSubmitIntent}
                className="w-full mt-4 rounded-xl bg-[#C857A8] hover:bg-[#B54E9A] text-white"
                data-testid="intent-continue-btn"
              >
                Continue
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Date Modal */}
      <Dialog open={showDateModal} onOpenChange={setShowDateModal}>
        <DialogContent className="max-w-sm rounded-xl">
          <DialogHeader>
            <DialogTitle>Log a date</DialogTitle>
            <DialogDescription>Are you meeting up? We'll check in afterwards - just for your safety.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowDateModal(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleLogDate} className="rounded-xl" data-testid="confirm-date-btn">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Flag Modal */}
      <Dialog open={showFlagModal} onOpenChange={setShowFlagModal}>
        <DialogContent className="max-w-sm rounded-xl">
          <DialogHeader>
            <DialogTitle>Report concern</DialogTitle>
            <DialogDescription>Something felt off during your date? This will be reviewed by our team.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowFlagModal(false)} className="rounded-xl">Cancel</Button>
            <Button variant="destructive" onClick={handleFlag} className="rounded-xl" data-testid="confirm-flag-btn">Report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-sm rounded-xl">
          <DialogHeader>
            <DialogTitle>Confirm report</DialogTitle>
            <DialogDescription>You're reporting this person for: <strong>{selectedReason}</strong></DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => { setShowReportDialog(false); setSelectedReason(null); }} className="rounded-xl">Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => selectedReason && handleReport(selectedReason)}
              className="rounded-xl"
              data-testid="confirm-report-btn"
            >
              Submit report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conversation Limit Modal */}
      <Dialog open={showConversationLimitModal} onOpenChange={setShowConversationLimitModal}>
        <DialogContent className="max-w-sm rounded-xl" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Conversation limit reached</DialogTitle>
            <DialogDescription>You're in 2 active conversations. Close one to start a new one.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => navigate('/messages')} className="rounded-xl">Go to messages</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exit Chat Dialog */}
      <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <DialogContent className="max-w-sm rounded-xl">
          <DialogHeader>
            <DialogTitle>End this conversation?</DialogTitle>
            <DialogDescription>This will permanently delete this chat and you won't see each other again. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowExitDialog(false)} className="rounded-xl" disabled={exitLoading}>Cancel</Button>
            <Button variant="destructive" onClick={handleExitChat} className="rounded-xl" disabled={exitLoading} data-testid="confirm-exit-btn">
              {exitLoading ? 'Ending...' : 'End Chat'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
